import ErrorResponse from '@/models/error/ErrorResponse'
import ErrorResponseType from '@/shared/constants/error/ErrorResponseType'
import { CreateBookingErrorCodes } from '@/shared/constants/booking/CreateBookingErrorCodes'

/**
 * Normalises a backend error from a result-helper response object into an ErrorResponse model.
 *
 * Validates that the response contains a well-formed error object before
 * trusting it. If the error is absent or malformed, a safe generic fallback
 * is returned instead.
 *
 * Maps to the backend ErrorResponse structure:
 *   { type, code, message, param, data }
 *
 * @param {Object|null} response - A result-helper response object (from fail() / success())
 * @param {Object} $i18n - Vue i18n instance
 * @returns {ErrorResponse}
 */
export default function normaliseErrorFromResponse(response, $i18n) {
  const error = response?.error
  const isValidError =
    error &&
    typeof error !== 'string' &&
    typeof error.code === 'string' &&
    typeof error.message === 'string'

  if (!isValidError) {
    return new ErrorResponse({
      _error: error,
      type: ErrorResponseType.unknown,
      code: CreateBookingErrorCodes.Unknown,
      message: $i18n.t('error.genericFailedRequestMessage'),
    })
  }

  return new ErrorResponse({
    _error: error,
    type: error.type ?? ErrorResponseType.unknown,
    code: error.code,
    message: error.message,
    param: error.param ?? null,
    data: error.data ?? null,
  })
}
